import { Box, Stack, Text, Title, Badge, Group } from "@mantine/core";
import { useOutletContext } from "react-router-dom";
import type { PrivateContext } from "@/components/layout/PrivateLayout";
import { releases } from "@/content/releases";
import { hasScope } from "@/lib/auth";
import { AudioPlayer } from "@/components/audio/AudioPlayer";
import { EmptyState } from "@/components/common/EmptyState";
import { formatDate, formatDuration } from "@/lib/format";

export default function PrivateUnreleased() {
  const { session } = useOutletContext<PrivateContext>();

  if (!hasScope(session, "unreleased-audio")) {
    return (
      <EmptyState
        title="Out of scope"
        body="Your token doesn't include `unreleased-audio`. Contact Alex if this is in error."
      />
    );
  }

  const forthcoming = releases.filter(
    (r) => r.status === "pre-release" || r.tracks.some((t) => t.releaseStatus === "forthcoming"),
  );

  if (forthcoming.length === 0) {
    return <EmptyState title="Nothing pre-release right now" />;
  }

  return (
    <Stack gap="lg">
      <Box>
        <Title order={1}>Unreleased</Title>
        <Text c="dimmed" mt="xs">
          Forthcoming masters. Full audio served via signed URLs.
        </Text>
      </Box>

      <Stack gap="md">
        {forthcoming.map((release) => (
          <Box
            key={release.slug}
            p="md"
            style={{
              background: "var(--color-deep)",
              border: "1px solid var(--color-ocean)",
              borderRadius: 12,
            }}
          >
            <Group justify="space-between" mb="xs">
              <Text fw={700}>{release.title}</Text>
              <Badge color="magenta" variant="light">
                {release.status} · {release.label}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed" mb="sm">
              {formatDate(release.releaseDate)}
            </Text>
            <Stack gap="xs">
              {release.tracks.map((t) => (
                <Box key={t.position}>
                  <Group justify="space-between" mb={4}>
                    <Text size="sm">
                      {t.position}. {t.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatDuration(t.durationSec)}
                    </Text>
                  </Group>
                  <AudioPlayer audio={t.master} session={session} />
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}
